import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import type { DocumentSection, FactSheet, Validation } from "@/api/schemas";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { DocSectionCard } from "./DocSectionCard";
import { SectionNav } from "./SectionNav";
import { FactsTab } from "./FactsTab";

interface DocTabsProps {
  izjave: DocumentSection[];
  naslovnice: DocumentSection[];
  tekstualni: DocumentSection[];
  tables: DocumentSection[];
  /** R8 — Popis mapa + Sadržaj Mape 1; tab shown only when non-empty. */
  mape?: DocumentSection[];
  factSheet: FactSheet;
  validation: Validation;
  /** Merged opći-dio variables — threaded to the izjava office PDF preview. */
  opciVariables?: Record<string, unknown>;
  /**
   * Sprint B — W6. When a j/k shortcut focuses a section that lives in
   * another tab, switch the tab automatically so the scroll-into-view
   * call in :func:`DocgenView.jumpToSection` actually lands on a
   * mounted DOM node.
   */
  focusedSectionId?: string | null;
}

const BASE_TAB_DEF = [
  { value: "izjave", label: "Izjave" },
  { value: "naslovnice", label: "Naslovnice" },
  { value: "tekst", label: "Tekstualni dio" },
  { value: "tablice", label: "Tablice / Iskazi" },
  { value: "mape", label: "Mape" },
  { value: "facts", label: "Činjenice" },
] as const;

type TabValue = typeof BASE_TAB_DEF[number]["value"];

export function DocTabs(props: DocTabsProps) {
  const mape = props.mape ?? [];
  // R8 — the Mape tab appears only when the project produced mape
  // documents (existing projects see the same 5 tabs as before).
  const TAB_DEF = useMemo(
    () => BASE_TAB_DEF.filter((t) => t.value !== "mape" || mape.length > 0),
    [mape.length],
  );
  const [active, setActive] = useState<TabValue>("izjave");
  const counts = useMemo(
    () => ({
      izjave: props.izjave.length,
      naslovnice: props.naslovnice.length,
      tekst: props.tekstualni.length,
      tablice: props.tables.length,
      mape: mape.length,
      facts: 1,
    }),
    [props, mape.length],
  );

  // Sprint B — W6. Whenever the focused section moves to a different
  // bucket (e.g. j-pressed past the end of "Izjave"), follow it.
  useEffect(() => {
    const id = props.focusedSectionId;
    if (!id) return;
    const tab = bucketOfSection(id, props);
    if (tab && tab !== active) setActive(tab);
    // ``props.focusedSectionId`` is the only meaningful trigger; the
    // section arrays are read once via ``bucketOfSection``.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.focusedSectionId]);

  return (
    <Tabs value={active} onValueChange={(v) => setActive(v as typeof active)}>
      <TabsList>
        {TAB_DEF.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
            {tab.value !== "facts" && (
              <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-stone-100 px-1.5 text-[0.7rem] font-bold text-stone-500 data-[state=active]:bg-ind-100 data-[state=active]:text-ind-700">
                {counts[tab.value]}
              </span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>

      {/* Sprint B — W6. AnimatePresence on the tab key gives a 200 ms
          fade/slide between panes — ``Tabs`` already swaps via Radix,
          but the framer wrapper smooths the content swap. */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          <TabsContent value="izjave" className="pt-4" forceMount={active === "izjave" ? true : undefined}>
            {active === "izjave" && <SectionPane sections={props.izjave} {...props} />}
          </TabsContent>
          <TabsContent value="naslovnice" className="pt-4" forceMount={active === "naslovnice" ? true : undefined}>
            {active === "naslovnice" && <SectionPane sections={props.naslovnice} {...props} />}
          </TabsContent>
          <TabsContent value="tekst" className="pt-4" forceMount={active === "tekst" ? true : undefined}>
            {active === "tekst" && <SectionPane sections={props.tekstualni} {...props} />}
          </TabsContent>
          <TabsContent value="tablice" className="pt-4" forceMount={active === "tablice" ? true : undefined}>
            {active === "tablice" && <SectionPane sections={props.tables} {...props} />}
          </TabsContent>
          {mape.length > 0 && (
            <TabsContent value="mape" className="pt-4" forceMount={active === "mape" ? true : undefined}>
              {active === "mape" && <SectionPane sections={mape} {...props} />}
            </TabsContent>
          )}
          <TabsContent value="facts" className="pt-4" forceMount={active === "facts" ? true : undefined}>
            {active === "facts" && (
              <FactsTab factSheet={props.factSheet} validation={props.validation} />
            )}
          </TabsContent>
        </motion.div>
      </AnimatePresence>
    </Tabs>
  );
}

interface SectionPaneProps {
  sections: DocumentSection[];
  validation: Validation;
  factSheet: FactSheet;
  opciVariables?: Record<string, unknown>;
}

function bucketOfSection(
  id: string,
  props: DocTabsProps,
): TabValue | null {
  if (props.izjave.some((s) => s.section_id === id)) return "izjave";
  if (props.naslovnice.some((s) => s.section_id === id)) return "naslovnice";
  if (props.tekstualni.some((s) => s.section_id === id)) return "tekst";
  if (props.tables.some((s) => s.section_id === id)) return "tablice";
  if ((props.mape ?? []).some((s) => s.section_id === id)) return "mape";
  return null;
}

function SectionPane({ sections, validation, factSheet, opciVariables }: SectionPaneProps) {
  if (!sections.length) {
    return (
      <p className="px-2 py-6 italic text-stone-400">Nema sekcija u ovom dijelu.</p>
    );
  }
  const showNav = sections.length >= 3;

  return (
    <div className={showNav ? "flex gap-6" : ""}>
      {showNav && <SectionNav sections={sections} validation={validation} />}
      <div className="flex-1 min-w-0">
        {sections.map((s, i) => (
          <DocSectionCard
            key={s.section_id}
            section={s}
            validation={validation}
            factSheet={factSheet}
            opciVariables={opciVariables}
            defaultOpen={i === 0}
          />
        ))}
      </div>
    </div>
  );
}
